import {
  Users,
  UserPlus,
  Settings,
  Headphones,
  Gamepad2,
  History,
  Code,
  Zap,
  Eye,
  Plus,
  Building2,
  type LucideIcon,
} from 'lucide-react'
import { NAV_GROUPS } from '@/lib/constants'

export type CommandType = 'navigation' | 'action' | 'settings' | 'integration'

export interface CommandEntry {
  id: string
  label: string
  description?: string
  icon: LucideIcon
  type: CommandType
  href?: string
  action?: string
  keywords?: string[]
  group: string
  superAdminOnly?: boolean
}

// Navigation commands derived from NAV_GROUPS
function getNavigationCommands(): CommandEntry[] {
  const extra: CommandEntry[] = [
    {
      id: 'nav-referral-network',
      label: 'Rede de Indicações',
      icon: UserPlus,
      type: 'navigation',
      href: '/referrals/network',
      keywords: ['network', 'rede', 'grafo'],
      group: 'Navegação',
    },
    {
      id: 'nav-playground',
      label: 'Playground',
      icon: Gamepad2,
      type: 'navigation',
      href: '/playground',
      keywords: ['teste', 'test', 'premium'],
      group: 'Navegação',
    },
  ]

  const fromNav: CommandEntry[] = NAV_GROUPS.flatMap((group) =>
    group.items.map((item) => ({
      id: `nav-${item.href.replace(/\//g, '-').slice(1)}`,
      label: item.title,
      icon: item.icon,
      type: 'navigation' as const,
      href: item.href,
      keywords: getKeywordsForNav(item.title),
      group: 'Navegação',
    }))
  )

  return [...fromNav, ...extra]
}

function getKeywordsForNav(title: string): string[] {
  const map: Record<string, string[]> = {
    Dashboard: ['painel', 'inicio', 'home', 'overview'],
    Analytics: ['métricas', 'graficos', 'charts', 'relatorio', 'creators analytics', 'performance'],
    Membros: ['members', 'indicadores', 'afiliados'],
    Indicações: ['referrals', 'indicação', 'referral'],
    Programa: ['campaigns', 'campanha', 'programa'],
    Creators: ['influenciadores', 'influencer', 'criadores'],
    'Social Listening': [
      'menções',
      'mentions',
      'instagram',
      'monitoramento',
      'post',
      'posts',
      'publicações',
      'conteudo',
      'conteúdo',
    ],
    Moderação: [
      'moderação',
      'moderacao',
      'comentários',
      'comments',
      'negativos',
      'ocultar',
      'hide',
      'sentiment',
    ],
  }
  return map[title] || []
}

// Settings commands
function getSettingsCommands(): CommandEntry[] {
  return [
    {
      id: 'settings-general',
      label: 'Geral',
      description: 'Configurações gerais da loja',
      icon: Settings,
      type: 'settings',
      href: '/settings/general',
      keywords: ['general', 'config', 'loja'],
      group: 'Configurações',
    },
    {
      id: 'settings-creators',
      label: 'Creators',
      description: 'Configurações de creators',
      icon: Users,
      type: 'settings',
      href: '/settings/creators',
      keywords: ['creator settings'],
      group: 'Configurações',
    },
    {
      id: 'settings-social-listening',
      label: 'Social Listening',
      description: 'Configurações de monitoramento',
      icon: Eye,
      type: 'settings',
      href: '/settings/social-listening',
      keywords: ['monitoramento', 'mentions'],
      group: 'Configurações',
    },
    {
      id: 'settings-integrations',
      label: 'Integrações',
      description: 'Gerencie conexões com ferramentas',
      icon: Zap,
      type: 'settings',
      href: '/settings/integrations',
      keywords: ['integrations', 'conectar'],
      group: 'Configurações',
    },
    {
      id: 'settings-team',
      label: 'Equipe',
      description: 'Gerencie membros da equipe',
      icon: Headphones,
      type: 'settings',
      href: '/settings/team',
      keywords: ['team', 'users', 'convite'],
      group: 'Configurações',
    },
    {
      id: 'settings-playground',
      label: 'Playground',
      description: 'Teste funcionalidades',
      icon: Gamepad2,
      type: 'settings',
      href: '/settings/playground',
      keywords: ['teste', 'test'],
      group: 'Configurações',
    },
    {
      id: 'settings-history',
      label: 'Histórico',
      description: 'Histórico de alterações',
      icon: History,
      type: 'settings',
      href: '/settings/history',
      keywords: ['log', 'audit', 'changes'],
      group: 'Configurações',
    },
    {
      id: 'settings-developers',
      label: 'Desenvolvedor',
      description: 'API keys e webhooks',
      icon: Code,
      type: 'settings',
      href: '/settings/developers',
      keywords: ['api', 'developer', 'webhook', 'chave'],
      group: 'Configurações',
    },
  ]
}

// Integration commands
function getIntegrationCommands(): CommandEntry[] {
  return [
    {
      id: 'integration-guru',
      label: 'Guru',
      description: 'Vendas e comissões automáticas',
      icon: Zap,
      type: 'integration',
      href: '/settings/integrations/guru',
      keywords: ['guru', 'vendas', 'comissões'],
      group: 'Integrações',
    },
    {
      id: 'integration-yampi',
      label: 'Yampi',
      description: 'Cupons e pedidos do e-commerce',
      icon: Zap,
      type: 'integration',
      href: '/settings/integrations/yampi',
      keywords: ['yampi', 'cupom', 'ecommerce'],
      group: 'Integrações',
    },
    {
      id: 'integration-klaviyo',
      label: 'Klaviyo',
      description: 'E-mails automáticos',
      icon: Zap,
      type: 'integration',
      href: '/settings/integrations/klaviyo',
      keywords: ['klaviyo', 'email', 'automação'],
      group: 'Integrações',
    },
    {
      id: 'integration-tiny',
      label: 'Tiny ERP',
      description: 'Envios e pedidos no Tiny',
      icon: Zap,
      type: 'integration',
      href: '/settings/integrations/tiny',
      keywords: ['tiny', 'erp', 'envio', 'pedido'],
      group: 'Integrações',
    },
  ]
}

// Action commands
function getActionCommands(): CommandEntry[] {
  return [
    {
      id: 'action-create-creator',
      label: 'Novo Creator',
      description: 'Criar um novo creator',
      icon: Plus,
      type: 'action',
      action: 'create-creator',
      keywords: ['criar', 'create', 'novo', 'adicionar'],
      group: 'Ações',
    },
    {
      id: 'action-create-campaign',
      label: 'Nova Campanha',
      description: 'Criar uma nova campanha',
      icon: Plus,
      type: 'action',
      action: 'create-campaign',
      keywords: ['criar', 'create', 'programa', 'campaign'],
      group: 'Ações',
    },
    {
      id: 'action-new-seeding',
      label: 'Novo Seeding',
      description: 'Enviar produtos para um creator',
      icon: Plus,
      type: 'action',
      href: '/creators',
      keywords: ['envio', 'produto', 'seeding', 'bonificação'],
      group: 'Ações',
    },
  ]
}

// Backoffice commands (super-admin only)
function getBackofficeCommands(): CommandEntry[] {
  return [
    {
      id: 'backoffice-merchants',
      label: 'Lojistas',
      description: 'Gerenciar lojistas',
      icon: Building2,
      type: 'navigation',
      href: '/backoffice/merchants',
      keywords: ['merchants', 'lojistas', 'backoffice', 'admin'],
      group: 'Backoffice',
      superAdminOnly: true,
    },
  ]
}

export function getAllCommands(isSuperAdmin = false): CommandEntry[] {
  return [
    ...getActionCommands(),
    ...getNavigationCommands(),
    ...getSettingsCommands(),
    ...getIntegrationCommands(),
    ...(isSuperAdmin ? getBackofficeCommands() : []),
  ]
}

export function getCommandById(id: string, isSuperAdmin = false): CommandEntry | undefined {
  return getAllCommands(isSuperAdmin).find((cmd) => cmd.id === id)
}
